import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { Styles as S } from '~/pages/anatomy/multipleChoice/anatomyCuriosity/styles'
import { Button } from '~/shared/components/Button'
import { OptionType } from '~/shared/constants/commonConstants'
import { useNextStep } from '~/shared/hooks/useNextStep'
import { TPageProps } from '~/shared/types/commonTypes'
import { shuffleArray } from '~/shared/utils'

const QUESTION = 'What sparks your curiosity most in anatomy?'

const OPTIONS = [
  { key: 'bonesAndMuscles', i18nKey: 'first' },
  { key: 'brainAndNerves', i18nKey: 'second' },
  { key: 'digestionAndGutHealth', i18nKey: 'third' },
  { key: 'hormonesAndEnergy', i18nKey: 'fourth' },
  { key: 'pelvicFloorHealth', i18nKey: 'fifth' },
  { key: 'other', i18nKey: 'sixth' },
] as const

const AnatomyCuriosity = ({ nextPagePath, pageId }: TPageProps) => {
  const { t } = useTranslation('anatomy')
  const [answers, setAnswers] = useState<string[]>([])
  const [shuffledOptions] = useState(() => [
    ...shuffleArray(OPTIONS.filter((o) => o.key !== 'other')),
    OPTIONS.find((o) => o.key === 'other')!,
  ])

  const handleOptionChange = (optionValue: string, isChecked: boolean) => {
    setAnswers((prev) =>
      isChecked
        ? [...prev, optionValue]
        : prev.filter((item) => item !== optionValue),
    )
  }

  const handleContinue = useNextStep({
    nextPagePath,
    pageId,
    pageName: pageId,
    question: QUESTION,
  })

  return (
    <S.Root>
      <S.StyledContainer>
        <S.ScrollContent>
          <S.Content>
            <S.Title>
              <Trans
                ns="anatomy"
                i18nKey="onboarding.multipleChoices.anatomyCuriosity.title"
              />
            </S.Title>
          </S.Content>

          {shuffledOptions.map(({ key, i18nKey }) => {
            const optionText = t(
              `onboarding.multipleChoices.anatomyCuriosity.options.${i18nKey}`,
            )
            const spaceIndex = optionText.indexOf(' ')
            const emoji =
              spaceIndex !== -1 ? optionText.slice(0, spaceIndex) : optionText
            const text =
              spaceIndex !== -1 ? optionText.slice(spaceIndex + 1) : ''

            return (
              <S.StyledOption
                key={key}
                type={OptionType.CHECKBOX}
                value={key}
                name="anatomyCuriosity"
                onChange={handleOptionChange}
                checked={answers.includes(key)}
              >
                <S.Value>
                  <S.OptionIcon>{emoji}</S.OptionIcon>
                  <S.ValueText>{text}</S.ValueText>
                </S.Value>
              </S.StyledOption>
            )
          })}
        </S.ScrollContent>

        <S.ButtonContainer>
          <Button
            type="button"
            onClick={() => handleContinue(answers)}
            disabled={!answers.length}
          >
            <Trans i18nKey="actions.continue" />
          </Button>
        </S.ButtonContainer>
      </S.StyledContainer>
    </S.Root>
  )
}

export default AnatomyCuriosity
